import { reportEvent as defaultReportEvent } from '@/app/utils';

export type LiveDebugValue = string | number | boolean | null | undefined;

export interface LiveDebugContext {
  page?: string;
  room_id?: string;
  rtc_user_id?: string;
  live_id?: string;
  avatar_id?: string;
  avatar_type?: 'ugc' | 'pgc';
}

const LOG_PREFIX = '[live-socket]';
const EVENT_PREFIX = 'live_debug_';
const STRING_FIELD_MAX_LEN = 200;
const IDENTIFIER_MAX_LEN = 128;
const URL_RE = /https?:\/\/\S+/gi;
const QUERY_RE = /\?[^\s]*/g;
// 32+ 位连续 base64ish / jwt 段视为潜在 token 隐去。
const TOKEN_RE = /[A-Za-z0-9+/=_-]{32,}/g;

const truncate = (raw: string, maxLen: number): string => (raw.length > maxLen ? `${raw.slice(0, maxLen)}…` : raw);

/**
 * 对可能包含敏感内容的自由文本字段脱敏：隐去 URL、query、疑似 token，并做长度截断。
 * 用于 error_message / raw_reason / connection_error 这类非标识字段；调用点显式使用，
 * logger 层不做自动 sanitize，避免误伤 correlation id / live_id / avatar_id 等标识字段。
 */
export const sanitizeString = (raw: string, maxLen = STRING_FIELD_MAX_LEN): string => {
  const cleaned = raw.replace(URL_RE, '<url>').replace(QUERY_RE, '?<query>').replace(TOKEN_RE, '<token>');
  return truncate(cleaned, maxLen);
};

const identifierGuard = (value: string): string => truncate(value, IDENTIFIER_MAX_LEN);

const flatten = (data: Record<string, LiveDebugValue> | undefined): Record<string, string | number | boolean> => {
  if (!data) {
    return {};
  }
  const out: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(data)) {
    if (value === undefined || value === null) {
      continue;
    }
    // 只做长度上限，不做 URL/query/token 脱敏——调用点自行判断字段类型。
    if (typeof value === 'string') {
      out[key] = truncate(value, STRING_FIELD_MAX_LEN);
      continue;
    }
    out[key] = value;
  }
  return out;
};

const filterFiniteMilestones = (raw: Record<string, number> | undefined): Record<string, number> => {
  if (!raw) {
    return {};
  }
  const out: Record<string, number> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (typeof value === 'number' && Number.isFinite(value)) {
      out[key] = value;
    }
  }
  return out;
};

export interface LiveDebugLogger {
  readonly correlationId: string;
  readonly isDisposed: boolean;
  attach(patch: LiveDebugContext): void;
  log(scope: string, event: string, data?: Record<string, LiveDebugValue>): void;
  /** 同一 key 只上报一次；用于 rtc_first_frame 这类跨重连也不应重复的事件。 */
  logOnce(key: string, scope: string, event: string, data?: Record<string, LiveDebugValue>): void;
  mark(milestone: string, timestamp?: number): void;
  /** 同一 milestone 只记一次，二次调用 no-op。 */
  markOnce(milestone: string, timestamp?: number): void;
  since(milestone: string): number | undefined;
  dispose(): void;
}

export interface CreateLiveDebugLoggerOptions {
  correlationId?: string;
  initialContext?: LiveDebugContext;
  initialMilestones?: Record<string, number>;
  now?: () => number;
  createCorrelationId?: () => string;
  reportEvent?: (eventName: string, properties?: Record<string, string | boolean | number>) => void;
  console?: Pick<Console, 'info'>;
}

const defaultNow = (): number => {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }
  return Date.now();
};

const defaultCreateCorrelationId = (): string => {
  try {
    const uuid = globalThis.crypto?.randomUUID?.();
    if (uuid) {
      return uuid;
    }
  } catch {
    // 非安全上下文 / 老浏览器：走 fallback。
  }
  return `sess_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
};

/**
 * 创建一次通话链路专属的 debug logger。
 * - dispose 后 log/mark/attach 全部 no-op，防止异步回调污染新通话。
 * - 标识字段（correlation id / live_id / avatar_id / scope）只做长度截断，保留原样。
 * - 自由文本字段的 URL/query/token 脱敏由调用点显式 `sanitizeString` 处理。
 * - console.info + reportEvent 双写，任一失败不影响业务。
 */
export const createLiveDebugLogger = (options: CreateLiveDebugLoggerOptions = {}): LiveDebugLogger => {
  const now = options.now ?? defaultNow;
  const createId = options.createCorrelationId ?? defaultCreateCorrelationId;
  const emit = options.reportEvent ?? defaultReportEvent;
  const consoleImpl = options.console ?? (typeof console !== 'undefined' ? console : undefined);
  const correlationId = identifierGuard(options.correlationId ?? createId());

  let context: LiveDebugContext = { ...(options.initialContext ?? {}) };
  let milestones: Record<string, number> = filterFiniteMilestones(options.initialMilestones);
  const emittedOnceKeys = new Set<string>();
  let disposed = false;

  const emitLog = (scope: string, event: string, data?: Record<string, LiveDebugValue>): void => {
    const eventName = `${EVENT_PREFIX}${event}`;
    const properties: Record<string, string | number | boolean> = {
      scope: identifierGuard(scope),
      session_correlation_id: correlationId,
      client_timestamp_ms: Date.now(),
      ...flatten(context as Record<string, LiveDebugValue>),
      ...flatten(data),
    };

    try {
      consoleImpl?.info(LOG_PREFIX, scope, event, properties);
    } catch {
      // 不影响业务。
    }

    try {
      emit(eventName, properties);
    } catch {
      // 不影响业务。
    }
  };

  return {
    correlationId,
    get isDisposed() {
      return disposed;
    },
    attach(patch) {
      if (disposed) {
        return;
      }
      context = { ...context, ...patch };
    },
    log(scope, event, data) {
      if (disposed) {
        return;
      }
      emitLog(scope, event, data);
    },
    logOnce(key, scope, event, data) {
      if (disposed || emittedOnceKeys.has(key)) {
        return;
      }
      emittedOnceKeys.add(key);
      emitLog(scope, event, data);
    },
    mark(milestone, timestamp) {
      if (disposed) {
        return;
      }
      const value = timestamp ?? now();
      if (Number.isFinite(value)) {
        milestones[milestone] = value;
      }
    },
    markOnce(milestone, timestamp) {
      if (disposed || milestone in milestones) {
        return;
      }
      const value = timestamp ?? now();
      if (Number.isFinite(value)) {
        milestones[milestone] = value;
      }
    },
    since(milestone) {
      if (disposed) {
        return undefined;
      }
      const start = milestones[milestone];
      if (start === undefined) {
        return undefined;
      }
      const elapsed = now() - start;
      return Number.isFinite(elapsed) && elapsed >= 0 ? Math.round(elapsed) : undefined;
    },
    dispose() {
      if (disposed) {
        return;
      }
      disposed = true;
      milestones = Object.freeze({ ...milestones });
      context = Object.freeze({ ...context });
    },
  };
};

/** 把 unknown error 拍平成上报友好字段；error_message 默认不带，显式 opt-in 且已脱敏。 */
export const describeError = (
  err: unknown,
  options: { includeMessage?: boolean } = {},
): Record<string, LiveDebugValue> => {
  if (err instanceof Error) {
    const errWithCode = err as Error & { code?: unknown };
    const code =
      typeof errWithCode.code === 'string' || typeof errWithCode.code === 'number' ? errWithCode.code : undefined;
    const base: Record<string, LiveDebugValue> = {
      error_name: err.name,
      error_code: code,
    };
    if (options.includeMessage && err.message) {
      base.error_message = sanitizeString(err.message);
    }
    return base;
  }
  if (typeof err === 'string') {
    return options.includeMessage ? { error_message: sanitizeString(err) } : { error_name: 'string' };
  }
  return { error_name: typeof err };
};
